import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ar'
import 'dayjs/locale/en'
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

import { useMarkNotificationRead } from '../../hooks/useNotifications'
import type { NotificationDto } from '../../services/notifications'
import StatusBadge, { type StatusTone } from '../ui/StatusBadge'

dayjs.extend(relativeTime)

interface Props {
  notification: NotificationDto
  /** When inside a Drawer/Popover, the parent passes this so we can close it on click. */
  onAfterClick?: () => void
  /** Compact = used in the Dashboard ActivityFeed (no padding bg, smaller). */
  compact?: boolean
}

const TONE_BY_TYPE: Record<string, StatusTone> = {
  info: 'info',
  success: 'success',
  warning: 'warn',
  danger: 'danger',
}

const ICON_BY_TYPE: Record<string, React.ReactNode> = {
  info: <InfoCircleOutlined />,
  success: <CheckCircleOutlined />,
  warning: <ExclamationCircleOutlined />,
  danger: <CloseCircleOutlined />,
}

/**
 * Single notification row. Used inside the bell's drawer and the Dashboard
 * Activity Feed — same component, just two slightly different paddings.
 */
export function NotificationItem({ notification, onAfterClick, compact = false }: Props) {
  const navigate = useNavigate()
  const markRead = useMarkNotificationRead()
  const tone: StatusTone = TONE_BY_TYPE[notification.type] ?? 'info'
  const icon = ICON_BY_TYPE[notification.type] ?? <BellOutlined />
  const unread = !notification.isRead

  const handleClick = () => {
    if (unread) markRead.mutate(notification.id)
    if (notification.link) navigate(notification.link)
    onAfterClick?.()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        all: 'unset',
        display: 'flex',
        gap: 12,
        padding: compact ? '10px 12px' : '12px 16px',
        cursor: 'pointer',
        width: '100%',
        borderRadius: 8,
        background: unread ? 'var(--aufq-accent-soft)' : 'transparent',
        border: '1px solid transparent',
        transition: 'background 0.12s ease, border-color 0.12s ease',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--aufq-line)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'transparent'
      }}
    >
      {/* Unread dot on the leading edge */}
      {unread ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            insetInlineStart: 4,
            top: '50%',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--aufq-accent)',
            transform: 'translateY(-50%)',
          }}
        />
      ) : null}

      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          display: 'grid',
          placeItems: 'center',
          background: `var(--aufq-${tone === 'warn' ? 'warn' : tone}-soft)`,
          color: `var(--aufq-${tone === 'warn' ? 'warn' : tone})`,
          flexShrink: 0,
          fontSize: 16,
        }}
      >
        {icon}
      </span>

      <div style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            marginBottom: 2,
          }}
        >
          <strong
            style={{
              fontWeight: unread ? 700 : 500,
              fontSize: 13,
              color: 'var(--aufq-ink)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {notification.title}
          </strong>
          <Tooltip title={dayjs(notification.createdAt).format('YYYY/MM/DD HH:mm')}>
            <time
              style={{
                fontSize: 11,
                color: 'var(--aufq-ink-3)',
                whiteSpace: 'nowrap',
                fontFamily: 'var(--aufq-font-num)',
                flexShrink: 0,
              }}
            >
              {dayjs(notification.createdAt).fromNow()}
            </time>
          </Tooltip>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 12.5,
            color: 'var(--aufq-ink-2)',
            lineHeight: 1.45,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {notification.body}
        </p>
        {notification.category ? (
          <span style={{ marginTop: 6, display: 'inline-block' }}>
            <StatusBadge tone={tone} size="sm" dot={false}>
              {notification.category}
            </StatusBadge>
          </span>
        ) : null}
      </div>
    </button>
  )
}

export default NotificationItem
